import {
  companyIdentifierChecks,
  linkedinCompanyHandle,
  websiteDomainOf,
} from "@/lib/company-mention-reliability";
import {
  getLogoPlatformIcon,
  getPlatformBrandColor,
} from "@/lib/platform-brand-colors";
import { sourceLink } from "@/lib/source-link";
import { digitsOnly } from "@/lib/string";
import type {
  CompanyDTO,
  CompanyTag,
  CompanyWebKeyData,
  CompanyWebSocialLink,
  CompanyWebSocialNetwork,
} from "@shaarp/common";
import { MapPinIcon } from "lucide-react";
import {
  RiFacebookCircleFill,
  RiFileLine,
  RiGlobalLine,
  RiInstagramLine,
  RiLinkedinFill,
  RiTiktokFill,
  RiTwitterXFill,
  RiYoutubeFill,
} from "react-icons/ri";

export type CompanyLink = {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  url: string;
  colorClass: string;
};

/** How each network is drawn: its icon and the name its link falls back to. */
const SOCIAL_NETWORKS: Record<
  CompanyWebSocialNetwork,
  { name: string; icon: React.ComponentType<{ className?: string }> }
> = {
  linkedin: { name: "LinkedIn", icon: RiLinkedinFill },
  instagram: {
    name: "Instagram",
    icon: getLogoPlatformIcon("instagram") ?? RiInstagramLine,
  },
  x: { name: "X", icon: RiTwitterXFill },
  facebook: { name: "Facebook", icon: RiFacebookCircleFill },
  youtube: { name: "YouTube", icon: RiYoutubeFill },
  tiktok: { name: "TikTok", icon: RiTiktokFill },
};

/** The link to a company account on `network`, labelled by its handle when known. */
function socialNetworkLink(
  network: CompanyWebSocialNetwork,
  url: string,
  handle?: string | null,
): CompanyLink {
  const { name, icon } = SOCIAL_NETWORKS[network];
  return {
    icon,
    label: handle ?? name,
    url,
    colorClass: getPlatformBrandColor(network),
  };
}

/** The company's pages on the registries a SIREN opens (Pappers, Societe.com). */
function registryLinks(siren: string): CompanyLink[] {
  return [
    {
      icon: getLogoPlatformIcon("pappers") ?? RiFileLine,
      label: "Pappers",
      url: `https://www.pappers.fr/entreprise/${siren}`,
      colorClass: "",
    },
    {
      icon: getLogoPlatformIcon("societe.com") ?? RiFileLine,
      label: "Societe.com",
      url: `https://www.societe.com/societe/-${siren}.html`,
      colorClass: "",
    },
  ];
}

function googleMapsLink(url: string): CompanyLink {
  return {
    icon: getLogoPlatformIcon("google maps") ?? MapPinIcon,
    label: "Google Maps",
    url,
    colorClass: "",
  };
}

/**
 * Builds a list of business registry and map links (Pappers, Societe.com, Google Maps).
 */
export function buildCompanyBusinessLinks(company: CompanyDTO): CompanyLink[] {
  const links: CompanyLink[] = [];

  if (company.business?.siren) {
    links.push(...registryLinks(company.business.siren));
  }
  if (company.links?.googleMaps) {
    links.push(googleMapsLink(company.links.googleMaps));
  }

  return links;
}

/**
 * Builds a list of social network links from a company DTO.
 */
export function buildCompanySocialLinks(company: CompanyDTO): CompanyLink[] {
  const networks = company.socialNetworks;
  const links: CompanyLink[] = [];

  if (networks?.linkedin?.url) {
    links.push(
      socialNetworkLink("linkedin", networks.linkedin.url, networks.linkedin.handle),
    );
  }
  if (networks?.instagram?.url) {
    links.push(
      socialNetworkLink("instagram", networks.instagram.url, networks.instagram.handle),
    );
  }
  if (networks?.x?.url) {
    links.push(socialNetworkLink("x", networks.x.url, networks.x.handle));
  }
  if (networks?.facebook?.url) {
    links.push(
      socialNetworkLink("facebook", networks.facebook.url, networks.facebook.handle),
    );
  }
  if (networks?.youtube?.url) {
    links.push(socialNetworkLink("youtube", networks.youtube.url));
  }
  if (networks?.tiktok?.url) {
    links.push(
      socialNetworkLink("tiktok", networks.tiktok.url, networks.tiktok.handle),
    );
  }

  return links;
}

/** The first SIREN of the tag or the profile whose check digit holds, read off a SIRET too. */
function validSirenOf(
  tag: Pick<CompanyTag, "siret" | "siren">,
  keyData: Partial<Pick<CompanyWebKeyData, "siret" | "siren">> | null | undefined,
): string | null {
  const candidates: ["siret" | "siren", string | null | undefined][] = [
    ["siret", tag.siret],
    ["siret", keyData?.siret],
    ["siren", tag.siren],
    ["siren", keyData?.siren],
  ];
  for (const [type, value] of candidates) {
    if (value && companyIdentifierChecks({ [type]: value })[type] === "valid") {
      return digitsOnly(value).slice(0, 9);
    }
  }
  return null;
}

/**
 * The links of the header of a company opened from its tag, in the order of
 * the company panel: its website, its social accounts, its registry pages and
 * a Google Maps search.
 *
 * The website is linked only when it is a bare domain, and labelled by the
 * host the link opens, never by the text the model wrote. The accounts are the
 * ones the web profile found and checked against its searches, at most one per
 * network; the tag's LinkedIn page stands in for a LinkedIn the profile lacks. The registries need a SIREN whose check digit
 * holds. With no Maps page known, the map is a search for the name and the
 * address (or the city) — never for the name alone, which lands on namesakes.
 */
export function buildCompanyTagLinks({
  tag,
  keyData,
  website,
  websiteLabel,
}: {
  tag: CompanyTag;
  keyData: Partial<CompanyWebKeyData> | null | undefined;
  website: string | null;
  /** The label of the website's link, for the host it opens. */
  websiteLabel: (domain: string) => string;
}): CompanyLink[] {
  const links: CompanyLink[] = [];

  // Model output on both sides: only a parsed http(s) URL becomes an href,
  // and the label says the host that URL opens.
  const domain = websiteDomainOf(website);
  const site = domain ? sourceLink(`https://${domain}`) : null;
  if (site) {
    links.push({
      icon: RiGlobalLine,
      label: websiteLabel(site.host),
      url: site.href,
      colorClass: "",
    });
  }

  const accounts: CompanyWebSocialLink[] = [...(keyData?.socialLinks ?? [])];
  const tagLinkedin = linkedinCompanyHandle(tag.linkedin);
  if (tagLinkedin && !accounts.some((account) => account.network === "linkedin")) {
    accounts.unshift({
      network: "linkedin",
      url: `https://www.linkedin.com/company/${tagLinkedin}`,
    });
  }
  for (const { network, url } of accounts) {
    const account = sourceLink(url);
    if (account && SOCIAL_NETWORKS[network]) {
      links.push(socialNetworkLink(network, account.href));
    }
  }

  const siren = validSirenOf(tag, keyData);
  if (siren) links.push(...registryLinks(siren));

  const place = keyData?.address ?? tag.address ?? tag.city;
  if (place) {
    const query = encodeURIComponent(`${tag.name} ${place}`);
    links.push(
      googleMapsLink(`https://www.google.com/maps/search/?api=1&query=${query}`),
    );
  }

  return links;
}
